import { CONFIGURATION_COMPONENT_LIST } from '@/helpers/constants';
import React, { Component, Suspense } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

@connect(state => ({
  querystring: state.querystring,
}))
class Configuration extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.toastRef = React.createRef();
    this.configComponentList = CONFIGURATION_COMPONENT_LIST;
  }

  componentDidMount() {
    this.handleAutoRedirect();
  }

  componentDidUpdate(prevProps) {
    // Check for auto-redirect when location changes
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.handleAutoRedirect();
    }
  }

  handleAutoRedirect = () => {
    const { location, history, querystring } = this.props;
    const currentPath = location.pathname;

    // If user is on exactly /configuration (base path), redirect to first config section
    if (currentPath === '/configuration') {
      const firstConfigSection = this.configComponentList[0]; // tenant-setting
      if (firstConfigSection) {
        // Build querystring for navigation
        let qString = '';
        if (querystring) {
          const querystringKeys = Object.keys(querystring);
          for (let i = 0; i < querystringKeys.length; i++) {
            const queryStrKey = querystringKeys[i];
            if (
              queryStrKey &&
              queryStrKey.includes('select-') &&
              querystring[queryStrKey]
            ) {
              qString += `${queryStrKey}=[${querystring[queryStrKey]}]&`;
            }
          }
        }
        // replace last character & with empty string if it exists
        if (qString && qString[qString.length - 1] === '&') {
          qString = qString.slice(0, -1);
        }

        // Redirect to first configuration section
        const redirectUrl = qString ? `${firstConfigSection.url}?${qString}` : firstConfigSection.url;
        history.replace(redirectUrl);
      }
    }
  };

  configButtonOnClicked = item => {
    // eslint-disable-next-line no-unused-vars
    const { index, name, component, url } = item;
    // get current querystring
    const { querystring, history } = this.props;

    // Build querystring for navigation
    let qString = '';
    if (querystring) {
      const querystringKeys = Object.keys(querystring);
      for (let i = 0; i < querystringKeys.length; i++) {
        const queryStrKey = querystringKeys[i];
        if (
          queryStrKey &&
          queryStrKey.includes('select-') &&
          querystring[queryStrKey]
        ) {
          qString += `${queryStrKey}=[${querystring[queryStrKey]}]&`;
        }
      }
    }
    // replace last character & with empty string if it exists
    if (qString && qString[qString.length - 1] === '&') {
      qString = qString.slice(0, -1);
    }

    // Use history.push instead of window.location.href for SPA navigation
    const fullUrl = qString ? `${url}?${qString}` : url;
    history.push(fullUrl);
  };

  getCurrentActiveIndex = () => {
    const { location } = this.props;
    const currentPath = location.pathname;

    // Find the matching component based on current path
    const activeIndex = this.configComponentList.findIndex(item =>
      currentPath.startsWith(item.url)
    );

    return activeIndex >= 0 ? activeIndex : 0;
  };

  getCurrentSectionComponent = () => {
    const { location } = this.props;
    const currentPath = location.pathname;

    // If on base /configuration, don't render content (will auto-redirect)
    if (currentPath === '/configuration') {
      return null;
    }

    // Find the matching component configuration
    const currentSection = this.configComponentList.find(item =>
      currentPath.startsWith(item.url)
    );

    if (currentSection) {
      const SectionComponent = SECTION_COMPONENTS[currentSection.component];
      return SectionComponent;
    }

    // Default to first component if no match found
    const defaultSection = this.configComponentList[0];
    return defaultSection ? SECTION_COMPONENTS[defaultSection.component] : null;
  };

  render() {
    const { location } = this.props;
    const currentPath = location.pathname;
    const activeIndex = this.getCurrentActiveIndex();
    const SectionComponent = this.getCurrentSectionComponent();

    // If on base /configuration path, show loading while redirect happens
    if (currentPath === '/configuration') {
      return (
        <div className="configuration-bg">
          <div className="tab-menu">
            {this.configComponentList.map((item, idx) => (
              <button
                key={item.name}
                className={`tab-btn${activeIndex === idx ? ' selected' : ''}`}
                onClick={() => this.configButtonOnClicked(item)}
                type="button"
              >
                {item.name}
              </button>
            ))}
          </div>
          <div className="config-window">
            <span>Loading...</span>
          </div>
        </div>
      );
    }

    return (
      <div className="configuration-bg">
        {/* Tab Selector - Always show when URL has /configuration prefix */}
        <div className="tab-menu">
          {this.configComponentList.map((item, idx) => (
            <button
              key={item.name}
              className={`tab-btn${activeIndex === idx ? ' selected' : ''}`}
              onClick={() => this.configButtonOnClicked(item)}
              type="button"
            >
              {item.name}
            </button>
          ))}
        </div>
        {/* Content Area - Show the current section */}
        <div className="config-window">
          <Suspense fallback={<span>Loading...</span>}>
            {SectionComponent ? (
              <SectionComponent />
            ) : (
              <span>No section found</span>
            )}
          </Suspense>
        </div>
      </div>
    );
  }
}

Configuration.propTypes = {};

// Map config component name to lazy-loaded section component
const SECTION_COMPONENTS = {
  TenantSetting: React.lazy(() => import('./TenantSetting/TenantSetting')),
  OrganizationSetting: React.lazy(() => import('./OrganizationSetting/OrganizationSetting')),
  LocationSetting: React.lazy(() => import('./LocationSetting/LocationSetting')),
  FloorSetting: React.lazy(() => import('./FloorSetting/FloorSetting')),
  AreaSetting: React.lazy(() => import('./AreaSetting/AreaSetting')),
  VerkadaDeviceSetting: React.lazy(() => import('./VerkadaDeviceSetting/VerkadaDeviceSetting')),
  DeviceLocationSetting: React.lazy(() => import('./DeviceLocationSetting/DeviceLocationSetting')),
  CameraZoneSetting: React.lazy(() => import('./CameraZoneSetting/CameraZoneSetting')),
  AirQualitySetting: React.lazy(() => import('./AirQualitySetting/AirQualitySetting')),
  EmployeeSetting: React.lazy(() => import('./EmployeeSetting/EmployeeSetting')),
  MeetingRoomAnalysisSetting: React.lazy(() => import('./MeetingRoomAnalysisSetting/MeetingRoomAnalysisSetting')),
  WorkingHourSetting: React.lazy(() => import('./WorkingHourSetting/WorkingHourSetting')),
  AttendanceRateSetting: React.lazy(() => import('./AttendanceRateSetting/AttendanceRateSetting')),
  PeopleCountSetting: React.lazy(() => import('./PeopleCountSetting/PeopleCountSetting')),
  UserSetting: React.lazy(() => import('./UserSetting/UserSetting')),
  AuditWriteLog: React.lazy(() => import('./AuditWriteLog/AuditWriteLog')),
  EmployeeTerminalSetting: React.lazy(() => import('./EmployeeTerminalSetting/EmployeeTerminalSetting')),
};

// Export with withRouter to get access to history, location, and match props
export default withRouter(Configuration);
